export interface CheckoffOperationFreshness {
  version: 2 | 3;
  physicalTime: number;
  logicalTime: string;
  instanceId: string;
  sequence: string;
}

export const CHECKOFF_OPERATION_TRANSPORT_MAX_UTF8_BYTES = 4096;

const MAX_SUPPORTED_PHYSICAL_TIME = 8_640_000_000_000_000;
const CANONICAL_DECIMAL = /^(?:0|[1-9][0-9]*)$/;
const INSTANCE_ID = /^[A-Za-z0-9_-]{1,128}$/;
const V2_LOGICAL_MAX = String(Number.MAX_SAFE_INTEGER - 1);
const V2_SEQUENCE_MAX = String(Number.MAX_SAFE_INTEGER);

function compareCanonicalDecimal(left: string, right: string): -1 | 0 | 1 {
  if (left.length !== right.length) return left.length < right.length ? -1 : 1;
  return left === right ? 0 : left < right ? -1 : 1;
}

function validPhysical(value: string): boolean {
  if (!CANONICAL_DECIMAL.test(value)) return false;
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric <= MAX_SUPPORTED_PHYSICAL_TIME;
}

function validV2Number(value: string): boolean {
  return CANONICAL_DECIMAL.test(value) && compareCanonicalDecimal(value, V2_LOGICAL_MAX) <= 0;
}

export function parseCheckoffOperationId(
  value: string | undefined,
): CheckoffOperationFreshness | undefined {
  if (typeof value !== "string") return undefined;
  const parts = value.split(":");
  if (parts.length !== 6 || parts[0] !== "checkoff") return undefined;
  const version = parts[1];
  if (version !== "v2" && version !== "v3") return undefined;
  if (!validPhysical(parts[2]!)) return undefined;
  if (!CANONICAL_DECIMAL.test(parts[3]!)) return undefined;
  if (version === "v2" && !validV2Number(parts[3]!)) return undefined;
  if (!INSTANCE_ID.test(parts[4]!) || !CANONICAL_DECIMAL.test(parts[5]!)) return undefined;
  if (version === "v2" && compareCanonicalDecimal(parts[5]!, V2_SEQUENCE_MAX) > 0) return undefined;
  return {
    version: version === "v2" ? 2 : 3,
    physicalTime: Number(parts[2]),
    logicalTime: parts[3]!,
    instanceId: parts[4]!,
    sequence: parts[5]!,
  };
}

export function compareCheckoffOperationFreshness(
  left: CheckoffOperationFreshness,
  right: CheckoffOperationFreshness,
): -1 | 0 | 1 {
  if (left.physicalTime !== right.physicalTime)
    return left.physicalTime < right.physicalTime ? -1 : 1;
  const logical = compareCanonicalDecimal(left.logicalTime, right.logicalTime);
  if (logical) return logical;
  if (left.instanceId !== right.instanceId)
    return left.instanceId < right.instanceId ? -1 : 1;
  return compareCanonicalDecimal(left.sequence, right.sequence);
}
